import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { NotificationOutlined, TagOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import CampaignSelectModal from 'containers/Rule/components/CampaignSelectModal';
import RewardSelectModal from 'containers/Rule/components/RewardSelectModal';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { wizardLinks } from '../wizardLinks';
import { PickedItem } from '../wizardModel';
import { wz, wzType } from '../wizardStyles';
import { PickerButton } from './controls';

const NewLink = styled(Link)`
  color: ${wz.accent};
  font-weight: 700;
  ${wzType.label}

  &:hover {
    color: ${wz.accent};
    text-decoration: underline;
  }
`;

/**
 * "Create a new …" beside a picker: the module's own builder, in a new tab, so the wizard and its
 * answers stay where they are; the picker lists the new record once it exists.
 */
export const CreateNewLink: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <NewLink to={to} target="_blank" rel="noopener noreferrer">
    {label}
  </NewLink>
);

interface IPickerProps {
  value: PickedItem | null;
  onChange: (value: PickedItem) => void;
  label: string;
}

/** "Select Reward" (6778:20567): the module's reward list; the button then names the pick. */
export const RewardPicker: React.FC<IPickerProps & { queryParams?: Record<string, unknown> }> = ({
  value,
  onChange,
  label,
  queryParams = {}
}) => {
  const { t } = useMultiLanguageTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <PickerButton
        icon={<TagOutlined />}
        label={value?.name ?? label}
        onClick={() => setOpen(true)}
        testId="orbit-wizard-reward-picker"
      />
      <CreateNewLink
        to={wizardLinks.createReward()}
        label={t(`${TOrbit}.wizard.createNew.reward`)}
      />
      {open ? (
        <RewardSelectModal
          isOpen
          isOutcome
          queryParams={queryParams}
          closeModal={() => setOpen(false)}
          setChoice={(_keys: number[], rows: { id: number; name: string }[]) => {
            const [reward] = rows;
            if (reward !== undefined) {
              onChange({ id: reward.id, name: reward.name });
            }
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
};

/** "Select Campaign" (6778:19828): the module's campaign list. */
export const CampaignPicker: React.FC<IPickerProps> = ({ value, onChange, label }) => {
  const { t } = useMultiLanguageTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <PickerButton
        icon={<NotificationOutlined />}
        label={value?.name ?? label}
        onClick={() => setOpen(true)}
        testId="orbit-wizard-campaign-picker"
      />
      <CreateNewLink
        to={wizardLinks.createCampaign()}
        label={t(`${TOrbit}.wizard.createNew.campaign`)}
      />
      {open ? (
        <CampaignSelectModal
          isOpen
          closeModal={() => setOpen(false)}
          onChooseCampaign={(campaign: { id: number; name: string }) => {
            onChange({ id: campaign.id, name: campaign.name });
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
